import { useAppDispatch, useAppSelector, useHandlePageNavigate } from "./hooks";
import {
  getNeighborhoodPOIsAsync,
  propertyReportDifferenceAsync,
  pullPropertyReportAsync,
  selectPropertyReportStatus,
  selectAllReportNotesList,
  preForeclosureSearchAsync,
  setPropertyReportPreForeclosureData,
  recentSaleSearchAsync,
  setPropertyReportRecentSalesData,
} from "../features/reports/reportsSlice";
import { useLocation } from "react-router-dom";
import { setPropertySearch } from "../features/multisearch/multisearchSlice";
import { selectPropertyMonitoring } from "../features/monitoring/monitoringSlice";
import { PropertyReport } from "../features/reports/reportTypes";
import { selectBrandInfo } from "../features/brand/brandSlice";
import {
  MemberExperience,
  selectMemberExperienceData,
} from "../features/authentication/authenticationSlice";
import { useGetSpecificReportNotesDispatch } from "./notesHook";
import React from "react";
import { getSpecificReportNotesParamBuilder } from "../utils";
import { sendMemberEventAsync } from "../features/admin/adminSlice";

export type PullPropertyReportParams = any;

type UsePropertyReportHook = () => (
  report: PullPropertyReportParams,
  pullLocation: string,
) => void;

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

/* Property Search */
const usePropertySearchDispatch: UsePropertyReportHook = () => {
  const dispatch = useAppDispatch();
  const propertyReportStatus = useAppSelector(selectPropertyReportStatus);
  const monitoredPropertyReports = useAppSelector(selectPropertyMonitoring);
  const memberExperienceData = useAppSelector(selectMemberExperienceData);
  const pageNavigate = useHandlePageNavigate();
  const location = useLocation();
  const brandInfo = useAppSelector(selectBrandInfo);

  const allReportNotesList = useAppSelector(selectAllReportNotesList);
  const getSpecificReportNotes = useGetSpecificReportNotesDispatch();

  const memberExperienceRef =
    React.useRef<MemberExperience>(memberExperienceData);

  React.useEffect(() => {
    memberExperienceRef.current = memberExperienceData;
  }, [memberExperienceData]);

  const query = useQuery();
  const version = query.get("version");

  const pullPropertyReport = React.useCallback(
    (report: PullPropertyReportParams, pullLocation: string) => {
      if (propertyReportStatus === "loading") return;
      if (location.pathname !== "/property-report") {
        pageNavigate("/property-report");
      } else {
        window.scrollTo(0, 0);
      }

      const dto = {
        addressLine1: report.streetNumber
          ? report.streetNumber + " " + report.streetName
          : report.fullStreetAddress
            ? report.fullStreetAddress
            : report.addressLine1
              ? report.addressLine1
              : report.streetName,
        addressLine2: report.subPremise
          ? report.subPremise
          : report.addressLine2,
        state: report.state,
        city: report.city,
        zip: report.zip ? report.zip : report.zip5,
        latitude: report.lat
          ? report.lat
          : report.latitude
            ? report.latitude
            : undefined,
        longitude: report.long
          ? report.long
          : report.longitude
            ? report.longitude
            : undefined,
        nearbySchoolRadius: report.nearbySchoolRadius
          ? report.nearbySchoolRadius
          : 5,
        propertyId: report.propertyId, // Optional property ID for FA data
        addressComponents: {
          streetNumber: report.streetNumber,
          streetName: report.streetName,
          subPremise: report.subPremise,
          zip4: report.zip4,
          county: report.county,
          neighborhood: report.neighborhood,
          sublocality: report.sublocality,
          premise: report.premise,
          floor: report.floor,
          establishment: report.establishment,
        },
        searchMethod: report.searchMethod,
      };

      dispatch(pullPropertyReportAsync(dto))
        .unwrap()
        .then(result => {
          if (result.success) {
            if (!result.noResults) {
              const report = result.report.results
                .scalableReport as PropertyReport;
              const monitoredReport = monitoredPropertyReports.find(
                monitoredProperty =>
                  monitoredProperty.addressLine1 === dto.addressLine1 &&
                  (!monitoredProperty.addressLine2 ||
                    monitoredProperty.addressLine2 === dto.addressLine2),
              );

              // first american data pull, use lat long and radius of 5 miles
              if (
                report.propertyDetails?.latitude &&
                report.propertyDetails?.longitude
              ) {
                dispatch(
                  preForeclosureSearchAsync({
                    latitude: report.propertyDetails.latitude,
                    longitude: report.propertyDetails.longitude,
                    radius: 5,
                    pageSize: 20,
                    context: "property",
                    preForeclosureSearchType: "property report",
                  }),
                );
                dispatch(
                  recentSaleSearchAsync({
                    latitude: report.propertyDetails.latitude,
                    longitude: report.propertyDetails.longitude,
                    pageNumber: 1,
                    pageSize: 20,
                  }),
                );
              } else {
                dispatch(setPropertyReportPreForeclosureData());
                dispatch(setPropertyReportRecentSalesData());
              }

              if (version && report.propertyReportSummary) {
                dispatch(
                  propertyReportDifferenceAsync({
                    ...dto,
                    versionA: Number(version),
                    versionB: Number(report.propertyReportSummary.version!),
                  }),
                );
              }

              if (
                !version &&
                monitoredReport &&
                report.propertyReportSummary &&
                monitoredReport.versionZ &&
                report.propertyReportSummary.version &&
                monitoredReport.versionZ !==
                  report.propertyReportSummary.version
              ) {
                dispatch(
                  propertyReportDifferenceAsync({
                    ...dto,
                    versionA: Number(monitoredReport.versionZ),
                    versionB: Number(report.propertyReportSummary.version!),
                  }),
                );
              }

              if (
                report &&
                report.propertyDetails &&
                ["pf"].includes(brandInfo.acronym)
              ) {
                if (report.propertyDetails?.postalCode) {
                  dispatch(
                    getNeighborhoodPOIsAsync({
                      zip: report.propertyDetails.postalCode,
                      zip4: report.propertyDetails.zip4,
                      variant: memberExperienceRef.current.variant,
                    }),
                  );
                }
              }
              let propertyReportParams = report
                ? getSpecificReportNotesParamBuilder(report)
                : undefined;
              //getSpecificReportNotes if property notes exist and notes for a specific property exist
              if (
                allReportNotesList?.some(
                  notes =>
                    notes &&
                    notes.reportType === "property" &&
                    notes.addressLine1 === report.propertyDetails?.addressLine1,
                ) &&
                report &&
                propertyReportParams
              ) {
                getSpecificReportNotes(propertyReportParams);
              }
            }
          }

          dispatch(setPropertySearch(dto));

          dispatch(
            sendMemberEventAsync({
              EventAction: "Search",
              EventCategory: "Property",
              MetaData: JSON.stringify({ "pull location": pullLocation }),
            }),
          );
        })
        .catch(error => {
          console.log(error.message);
          pageNavigate("/");
        });
    },
    [
      dispatch,
      propertyReportStatus,
      monitoredPropertyReports,
      version,
      brandInfo,
      pageNavigate,
      location,
    ],
  );

  return pullPropertyReport;
};

export { usePropertySearchDispatch };
